import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const SpecialBusinessHoursDeleteDialog = ({
  open,
  item,
  loading,
  onClose,
  onConfirm,
  lang,
}) => {
  const isFrench = lang === "FR";

  if (!item) {
    return null;
  }

  // ============================================================
  // Helpers
  // ============================================================

  const formatDate = (date) => {
    if (!date) {
      return "";
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString(isFrench ? "fr-CA" : "en-CA", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (time) => {
    if (!time) {
      return "";
    }

    // Handles values such as:
    // "08:00:00"
    // "08:00"
    return time.substring(0, 5);
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    onClose();
  };

  // ============================================================
  // Render
  // ============================================================

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "error.lighter",
              color: "error.main",
              border: "1px solid",
              borderColor: "error.light",
            }}
          >
            <DeleteOutlineRoundedIcon />
          </Box>

          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                lineHeight: 1.2,
              }}
            >
              {isFrench
                ? "Supprimer l'horaire spécial"
                : "Delete Special Hours"}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              {isFrench ? "Confirmation de suppression" : "Delete confirmation"}
            </Typography>
          </Box>
        </Stack>

        <Button
          onClick={handleClose}
          disabled={loading}
          variant="text"
          color="inherit"
          sx={{
            minWidth: 40,
            width: 40,
            height: 40,
            borderRadius: 2,
            p: 0,
          }}
        >
          <CloseRoundedIcon />
        </Button>
      </Box>

      {/* ======================================================
          CONTENT
      ====================================================== */}

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Stack spacing={2.5}>
          {/* Warning */}

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: "warning.lighter",
              border: "1px solid",
              borderColor: "warning.light",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <WarningAmberRoundedIcon color="warning" sx={{ mt: 0.2 }} />

              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="warning.dark"
                >
                  {isFrench ? "Attention" : "Warning"}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.4 }}
                >
                  {isFrench
                    ? "Cette action supprimera définitivement cet horaire spécial. Cette opération ne peut pas être annulée."
                    : "This action will permanently delete this special business hour. This operation cannot be undone."}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Question */}

          <Box>
            <Typography variant="body1" fontWeight={600}>
              {isFrench
                ? "Voulez-vous vraiment supprimer cet horaire ?"
                : "Are you sure you want to delete this special hour?"}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {isFrench
                ? "Vérifiez les informations ci-dessous avant de confirmer."
                : "Please review the information below before confirming."}
            </Typography>
          </Box>

          {/* ==================================================
              SPECIAL HOURS SUMMARY
          ================================================== */}

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            {/* Date */}

            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 1.5,
                  backgroundColor: "action.hover",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <CalendarMonthRoundedIcon color="primary" />
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {isFrench ? "Date" : "Date"}
                </Typography>

                <Typography variant="body1" fontWeight={700} sx={{ mt: 0.2 }}>
                  {formatDate(item.date)}
                </Typography>
              </Box>
            </Box>

            <Divider />

            {/* Opening / Closing */}

            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              {/* Opening */}

              <Stack direction="row" spacing={1.2} alignItems="center">
                <AccessTimeRoundedIcon fontSize="small" color="success" />

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isFrench ? "Ouverture" : "Opening"}
                  </Typography>

                  <Typography variant="body1" fontWeight={700}>
                    {formatTime(item.opening)}
                  </Typography>
                </Box>
              </Stack>

              {/* Arrow */}

              <Typography color="text.disabled" sx={{ fontWeight: 700 }}>
                →
              </Typography>

              {/* Closing */}

              <Stack direction="row" spacing={1.2} alignItems="center">
                <AccessTimeRoundedIcon fontSize="small" color="error" />

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isFrench ? "Fermeture" : "Closing"}
                  </Typography>

                  <Typography variant="body1" fontWeight={700}>
                    {formatTime(item.closing)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Stack>
      </DialogContent>

      {/* ======================================================
          ACTIONS
      ====================================================== */}

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          color="inherit"
          startIcon={<CloseRoundedIcon />}
          sx={{
            borderRadius: 2,
            px: 2.5,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {isFrench ? "Annuler" : "Cancel"}
        </Button>

        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color="error"
          startIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <DeleteOutlineRoundedIcon />
            )
          }
          sx={{
            borderRadius: 2,
            px: 2.5,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            minWidth: 130,
          }}
        >
          {loading
            ? isFrench
              ? "Suppression..."
              : "Deleting..."
            : isFrench
              ? "Supprimer"
              : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpecialBusinessHoursDeleteDialog;
